package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.AccountBalanceWallet
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminPaymentMethod
import org.gymly.model.AdminStoreSaleMethod
import org.gymly.model.AdminClientAccountApplyCredit
import org.gymly.model.AdminClientAccountStatement
import org.gymly.theme.AdminColors
import org.gymly.util.GymlyCurrency
import org.gymly.util.formatMoney
import org.gymly.util.userMessage

@Composable
fun ClientAccountCreditBanner(
    clientId: String?,
    pendingAmountCents: Long,
    membershipOrderId: String? = null,
    salesOrderId: String? = null,
    enabled: Boolean = true,
    /** Si true, reserva el saldo en UI y aplica al guardar (nueva orden sin id aún). */
    deferApply: Boolean = false,
    highlighted: Boolean = false,
    initialAppliedCents: Long = 0L,
    onCreditApplied: (appliedCents: Long, balanceAfterCents: Long) -> Unit = { _, _ -> },
    onBalanceLoaded: (balanceCents: Long) -> Unit = {},
) {
    if (clientId.isNullOrBlank() || pendingAmountCents <= 0) return
    if (!deferApply && membershipOrderId.isNullOrBlank() && salesOrderId.isNullOrBlank()) return

    var moduleEnabled by remember { mutableStateOf(false) }
    var statement by remember(clientId) { mutableStateOf<AdminClientAccountStatement?>(null) }
    var loading by remember(clientId) { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var deferredUsedCents by remember(clientId, deferApply, initialAppliedCents) {
        mutableStateOf(initialAppliedCents.coerceAtLeast(0L))
    }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        moduleEnabled = runCatching {
            AdminRepo.api.getSettingsModules().clientAccountsEnabled
        }.getOrDefault(false)
    }

    LaunchedEffect(clientId, moduleEnabled) {
        if (!moduleEnabled) {
            statement = null
            loading = false
            error = null
            return@LaunchedEffect
        }
        loading = true
        error = null
        deferredUsedCents = initialAppliedCents.coerceAtLeast(0L)
        statement = runCatching { AdminRepo.api.clientAccountStatement(clientId) }
            .onFailure { error = it.userMessage("No se pudo cargar el monedero digital.") }
            .getOrNull()
        loading = false
    }

    LaunchedEffect(initialAppliedCents) {
        deferredUsedCents = initialAppliedCents.coerceAtLeast(0L)
    }

    val balance = statement?.balanceCents ?: 0L
    LaunchedEffect(balance, moduleEnabled) {
        if (moduleEnabled) onBalanceLoaded(balance)
    }

    if (!moduleEnabled) return
    if (balance <= 0L && !loading && error == null) return

    val availableBalance = (balance - deferredUsedCents).coerceAtLeast(0L)
    val applicable = minOf(availableBalance, pendingAmountCents)
    val currency = statement?.currency ?: GymlyCurrency.CODE

    var amountInput by remember(clientId) { mutableStateOf("") }
    val requestedCents = parseBulkAmountToCents(amountInput)
    val amountToApply = if (amountInput.isBlank()) applicable else minOf(requestedCents, applicable)
    val amountExceeds = requestedCents > applicable

    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(if (highlighted) 14.dp else 10.dp))
            .background(AdminColors.Primary.copy(alpha = if (highlighted) 0.16f else 0.08f))
            .then(
                if (highlighted) {
                    Modifier.border(2.dp, AdminColors.Primary.copy(alpha = 0.65f), RoundedCornerShape(14.dp))
                } else {
                    Modifier
                },
            )
            .padding(if (highlighted) 16.dp else 12.dp),
    ) {
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            Icon(
                Icons.Outlined.AccountBalanceWallet,
                contentDescription = null,
                tint = AdminColors.Primary,
                modifier = Modifier.size(if (highlighted) 26.dp else 20.dp),
            )
            Spacer(Modifier.width(10.dp))
            Column(Modifier.weight(1f)) {
                Text(
                    "Monedero digital",
                    fontWeight = FontWeight.Bold,
                    fontSize = if (highlighted) 15.sp else 13.sp,
                    color = AdminColors.Text,
                )
                if (highlighted) {
                    Text(
                        "Saldo a favor del socio — puedes usarlo en este cobro.",
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                    )
                }
            }
            if (!loading && availableBalance > 0) {
                Text(
                    formatMoney(availableBalance.toInt(), currency),
                    fontWeight = FontWeight.Bold,
                    fontSize = if (highlighted) 20.sp else 14.sp,
                    color = AdminColors.Primary,
                )
            }
        }
        Spacer(Modifier.height(if (highlighted) 10.dp else 4.dp))
        when {
            loading -> Text("Cargando saldo…", color = AdminColors.TextMuted, fontSize = 12.sp)
            error != null -> Text(error!!, color = AdminColors.Error, fontSize = 12.sp)
            availableBalance > 0 -> {
                Text(
                    "Aplicable a este cobro: ${formatMoney(applicable.toInt(), currency)}",
                    color = AdminColors.Text,
                    fontSize = if (highlighted) 13.sp else 12.sp,
                    fontWeight = if (highlighted) FontWeight.Medium else FontWeight.Normal,
                )
                if (deferApply && deferredUsedCents > 0) {
                    Spacer(Modifier.height(4.dp))
                    Text(
                        "Reservado para este cobro: ${formatMoney(deferredUsedCents.toInt(), currency)}",
                        color = AdminColors.Primary,
                        fontSize = 12.sp,
                    )
                }
                Spacer(Modifier.height(10.dp))
                Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.Bottom) {
                    Column(Modifier.weight(1f)) {
                        Text(
                            "Monto a usar (${GymlyCurrency.SYMBOL})",
                            color = AdminColors.TextMuted,
                            fontSize = 12.sp,
                        )
                        Spacer(Modifier.height(4.dp))
                        OutlinedTextField(
                            value = amountInput,
                            onValueChange = { amountInput = it },
                            enabled = enabled && !loading,
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true,
                            placeholder = {
                                Text(
                                    centsToAmountInput(applicable),
                                    color = AdminColors.TextMuted,
                                )
                            },
                            shape = RoundedCornerShape(10.dp),
                            colors = OutlinedTextFieldDefaults.colors(
                                focusedBorderColor = AdminColors.Primary,
                                unfocusedBorderColor = AdminColors.Outline,
                            ),
                        )
                    }
                    Spacer(Modifier.width(8.dp))
                    TextButton(
                        onClick = { amountInput = centsToAmountInput(applicable) },
                        enabled = enabled && !loading,
                    ) {
                        Text("Todo", color = AdminColors.Primary, fontWeight = FontWeight.SemiBold, fontSize = 12.sp)
                    }
                }
                Spacer(Modifier.height(4.dp))
                Text(
                    when {
                        amountExceeds ->
                            "El máximo aplicable es ${formatMoney(applicable.toInt(), currency)}; se usará ese monto."
                        amountInput.isBlank() ->
                            "Déjalo vacío para usar todo lo aplicable, o escribe una parte."
                        else ->
                            "Se usará ${formatMoney(amountToApply.toInt(), currency)} y el resto se cobra con otros métodos."
                    },
                    color = if (amountExceeds) AdminColors.Error else AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
                Spacer(Modifier.height(8.dp))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Button(
                        onClick = {
                            val applied = amountToApply
                            if (applied <= 0) return@Button
                            amountInput = ""
                            if (deferApply) {
                                deferredUsedCents += applied
                                onCreditApplied(applied, availableBalance - applied)
                            } else {
                                scope.launch {
                                    loading = true
                                    error = null
                                    runCatching {
                                        AdminRepo.api.applyClientAccountCredit(
                                            clientId,
                                            AdminClientAccountApplyCredit(
                                                membershipOrderId = membershipOrderId,
                                                salesOrderId = salesOrderId,
                                                amountCents = applied,
                                            ),
                                        )
                                    }.onSuccess { result ->
                                        onCreditApplied(result.appliedCents, result.balanceAfterCents)
                                        statement = runCatching { AdminRepo.api.clientAccountStatement(clientId) }.getOrNull()
                                    }.onFailure {
                                        error = it.userMessage("No se pudo aplicar el saldo.")
                                    }
                                    loading = false
                                }
                            }
                        },
                        enabled = enabled && !loading && amountToApply > 0,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text(
                            "Usar ${formatMoney(amountToApply.toInt(), currency)}",
                            fontSize = if (highlighted) 14.sp else 12.sp,
                            fontWeight = FontWeight.SemiBold,
                        )
                    }
                    Spacer(Modifier.width(8.dp))
                    Text(
                        if (deferApply) {
                            "Se aplicará al guardar la orden."
                        } else {
                            "Reduce el monto pendiente de cobro."
                        },
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                    )
                }
            }
        }
    }
}

/**
 * Solo informa el saldo a favor del socio (sin acción). Para avisar al elegir el socio
 * que tendrá monedero disponible cuando configure el cobro.
 */
@Composable
fun ClientWalletBalanceHint(
    clientId: String?,
    modifier: Modifier = Modifier,
) {
    if (clientId.isNullOrBlank()) return
    var moduleEnabled by remember { mutableStateOf(false) }
    var balance by remember(clientId) { mutableStateOf(0L) }
    var currency by remember(clientId) { mutableStateOf(GymlyCurrency.CODE) }

    LaunchedEffect(clientId) {
        moduleEnabled = runCatching {
            AdminRepo.api.getSettingsModules().clientAccountsEnabled
        }.getOrDefault(false)
        if (!moduleEnabled) {
            balance = 0L
            return@LaunchedEffect
        }
        val statement = runCatching { AdminRepo.api.clientAccountStatement(clientId) }.getOrNull()
        balance = statement?.balanceCents ?: 0L
        currency = statement?.currency ?: GymlyCurrency.CODE
    }

    if (!moduleEnabled || balance <= 0L) return

    Row(
        modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .background(AdminColors.Primary.copy(alpha = 0.10f))
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Icon(
            Icons.Outlined.AccountBalanceWallet,
            contentDescription = null,
            tint = AdminColors.Primary,
            modifier = Modifier.size(20.dp),
        )
        Spacer(Modifier.width(8.dp))
        Column(Modifier.weight(1f)) {
            Text(
                "Monedero digital: ${formatMoney(balance.toInt(), currency)}",
                fontWeight = FontWeight.Bold,
                fontSize = 13.sp,
                color = AdminColors.Text,
            )
            Text(
                "Podrás usar este saldo al configurar el cobro del socio.",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
            )
        }
    }
}

fun AdminPaymentMethod.toStoreSaleMethod(): AdminStoreSaleMethod = when (this) {
    AdminPaymentMethod.CASH -> AdminStoreSaleMethod.CASH
    AdminPaymentMethod.CARD -> AdminStoreSaleMethod.CARD
    AdminPaymentMethod.MOBILE_PAYMENT -> AdminStoreSaleMethod.MOBILE_PAYMENT
    AdminPaymentMethod.TRANSFER -> AdminStoreSaleMethod.TRANSFER
    AdminPaymentMethod.ZELLE -> AdminStoreSaleMethod.ZELLE
    AdminPaymentMethod.USDT -> AdminStoreSaleMethod.USDT
    AdminPaymentMethod.DIVISAS -> AdminStoreSaleMethod.DIVISAS
    AdminPaymentMethod.BIZUM -> AdminStoreSaleMethod.TRANSFER
    AdminPaymentMethod.ONLINE -> AdminStoreSaleMethod.CARD
}

fun AdminStoreSaleMethod.toPaymentMethod(): AdminPaymentMethod = when (this) {
    AdminStoreSaleMethod.CASH -> AdminPaymentMethod.CASH
    AdminStoreSaleMethod.CARD -> AdminPaymentMethod.CARD
    AdminStoreSaleMethod.MOBILE_PAYMENT -> AdminPaymentMethod.MOBILE_PAYMENT
    AdminStoreSaleMethod.TRANSFER -> AdminPaymentMethod.TRANSFER
    AdminStoreSaleMethod.ZELLE -> AdminPaymentMethod.ZELLE
    AdminStoreSaleMethod.PAYPAL -> AdminPaymentMethod.ONLINE
    AdminStoreSaleMethod.USDT -> AdminPaymentMethod.USDT
    AdminStoreSaleMethod.DIVISAS -> AdminPaymentMethod.DIVISAS
}
